import {useEffect, useState} from 'react';

import {getViewTeams} from '/redux/action/myTeams.action';

import {useMyTeamsContext} from './hooks/useMyTeamsContext';
import {CoachInformation} from './TeamOrgReport';

export function useCoachList({
  item,
  filtersData,
  reportParams,
  rootCoachId,
  setIsChevronPressed,
}: any) {
  const [data, setData] = useState<CoachInformation[]>(item);
  const [visibleMap, setVisibleMap] = useState(new Map());
  const [countsMap, setCountsMap] = useState(new Map());
  const [pageMap, setPageMap] = useState(new Map());
  const [loadingIds, setLoadingIds] = useState(new Set());
  const [childMap, setChildMap] = useState(new Map());
  const [isPaginating, setIsPaginating] = useState(false);

  const {dispatch} = useMyTeamsContext();
  // Helper: deduplicate coaches by ID
  const deduplicateCoaches = (
    coaches: CoachInformation[],
  ): CoachInformation[] => {
    const seen = new Set();
    return coaches.filter(coach => {
      if (seen.has(coach.id)) return false;
      seen.add(coach.id);
      return true;
    });
  };

  useEffect(() => {
    setData(deduplicateCoaches(item || []));
    setVisibleMap(new Map());
    setChildMap(new Map());
    setCountsMap(new Map());
  }, [item]);

  useEffect(() => {
    if (Array.isArray(item)) {
      const rootCoach = item.find(c => c.isParentCoach === true);
      if (rootCoach) {
        const children = item.filter(c => !c.isParentCoach);
        setVisibleMap(new Map([[rootCoach.id, children.length]]));
        setChildMap(new Map([[rootCoach.id, deduplicateCoaches(children)]]));
        setCountsMap(new Map([[rootCoach.id, item.length]]));
      }
    }
  }, [item]);

  const toggleExpand = async (id: number | string, level: number) => {
    setIsChevronPressed(true);
    const nextLevel = level + 1;
    const node = data.find(c => c.id === id);
    const isRootParent = Boolean(node?.isParentCoach) && level === 0;

    const currentShown = visibleMap.get(id) ?? 0;
    const isExpanded = currentShown > 0;

    if (isExpanded) {
      setVisibleMap(prev => {
        const next = new Map(prev);
        next.set(id, 0);
        return next;
      });
      return;
    }

    if (isRootParent) {
      const localChildren = data.filter(
        c => (c.level?.level ?? 0) === nextLevel && !c.isParentCoach,
      );
      setChildMap(prev =>
        new Map(prev).set(id, deduplicateCoaches(localChildren)),
      );
      setVisibleMap(prev => new Map(prev).set(id, localChildren.length));
      return;
    }

    if (childMap.has(id)) {
      const cached = childMap.get(id) ?? [];
      setVisibleMap(prev => new Map(prev).set(id, cached.length));
      return;
    }

    try {
      setLoadingIds(prev => new Set(prev).add(id));
      const currentPage = pageMap.get(id) ?? 0;
      const nextPage = currentPage + 1;
      const response = await dispatch(
        getViewTeams({
          coachId: id,
          pageNumber: nextPage,
          reportParams: [{...filtersData, ...reportParams?.[0]}],
          appendData: false,
          isDrilldown: true,
        }),
      );
      const children = response?.myTeams || [];
      const reportCount = response?.reportFilterCount_DrillDown ?? 0;

      const viewTeamsChildren: CoachInformation[] = children
        .filter(coach => !coach.isParentCoach)
        .map(coach => ({
          ...coach,
          level: {level: nextLevel},
          isViewTeam: coach.isViewTeam ?? false,
        }));

      setChildMap(prev => {
        const existing = prev.get(id) ?? [];
        const updated = deduplicateCoaches([...existing, ...viewTeamsChildren]);
        return new Map(prev).set(id, updated);
      });

      setVisibleMap(prev => new Map(prev).set(id, viewTeamsChildren.length));
      setCountsMap(prev => new Map(prev).set(id, reportCount - 1));
      setPageMap(prev => new Map(prev).set(id, nextPage));
    } finally {
      setIsChevronPressed(false);
      setLoadingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const loadMore = async (
    id: number | string,
    level: number,
    isViewTeam: boolean,
  ) => {
    const effectiveId = isViewTeam && level === 0 ? rootCoachId : id;
    setIsPaginating(true);
    try {
      setLoadingIds(prev => new Set(prev).add(effectiveId));
      const currentPage = pageMap.get(effectiveId) ?? 1;
      const nextPage = currentPage + 1;
      const node = data.find(c => c.id === effectiveId);
      const isRootParent = Boolean(node?.isParentCoach) && level === 0;

      if (isRootParent && !isViewTeam) {
        const nextLevel = level + 1;
        const allRootChildren = data.filter(
          c => (c.level?.level ?? 0) === nextLevel && !c.isParentCoach,
        );

        setChildMap(prev => {
          const existing = prev.get(id) ?? [];
          const defaultBatchSize = 10;
          const remaining = allRootChildren.length - existing.length;
          const nextBatchSize = Math.min(defaultBatchSize, remaining);
          const updated = allRootChildren.slice(
            0,
            existing.length + nextBatchSize,
          );
          const delta = updated.length - existing.length;
          if (delta <= 0) return prev;

          setVisibleMap(v => new Map(v).set(id, updated.length));
          return new Map(prev).set(id, deduplicateCoaches(updated));
        });

        setPageMap(prev => new Map(prev).set(id, nextPage));
      } else {
        const response = await dispatch(
          getViewTeams({
            coachId: effectiveId,
            pageNumber: nextPage,
            reportParams: [{...filtersData, ...reportParams?.[0]}],
            appendData: true,
            isDrilldown: level != 0,
          }),
        );

        const children = response?.myTeams;

        if (!Array.isArray(children)) return;

        const viewTeamsChildren: CoachInformation[] = children
          .filter(coach => !coach.isParentCoach)
          .map(coach => ({
            ...coach,
            level: {level: level + 1},
            isViewTeam: coach.isViewTeam ?? false,
          }));

        setChildMap(prev => {
          const existing = prev.get(id) ?? [];
          const updated = deduplicateCoaches([
            ...existing,
            ...viewTeamsChildren,
          ]);
          return new Map(prev).set(id, updated);
        });

        setVisibleMap(prev =>
          new Map(prev).set(
            effectiveId,
            (prev.get(id) ?? 0) + viewTeamsChildren.length,
          ),
        );

        const reportCount =
          level === 0
            ? response?.reportFilterCount ?? 0
            : response?.reportFilterCount_DrillDown ?? 0;
        setCountsMap(prev => new Map(prev).set(effectiveId, reportCount - 1));
        setPageMap(prev => new Map(prev).set(effectiveId, nextPage));
      }
    } finally {
      setIsPaginating(false);
      setLoadingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  return {
    data,
    visibleMap,
    toggleExpand,
    loadingIds,
    childMap,
    loadMore,
    countsMap,
    isPaginating,
  };
}
